import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { Shield, Flag, Package, Users, AlertTriangle, Zap, CheckCircle, Eye } from "lucide-react";
import { toast } from "sonner";

const STAT_CARDS = [
  { key: "active_listings", label: "Active", icon: Package },
  { key: "removed_listings", label: "Removed", icon: AlertTriangle },
  { key: "total_users", label: "Users", icon: Users },
  { key: "open_reports", label: "Open Reports", icon: Flag },
  { key: "boosted_listings", label: "Boosted", icon: Zap },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [reports, setReports] = useState([]);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/listings").then((r) => setListings(r.data));
    api.get("/admin/reports").then((r) => setReports(r.data));
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (lid, status) => { await api.put(`/admin/listings/${lid}/status`, { status }); toast.success(`Listing ${status}`); load(); };
  const resolve = async (rid) => { await api.put(`/admin/reports/${rid}/resolve`); toast.success("Report resolved"); load(); };

  return (
    <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center gap-2 mb-6">
        <Shield className="w-6 h-6 text-blaze" />
        <h1 className="font-heading font-900 text-3xl uppercase tracking-tight">Moderation</h1>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8">
        {STAT_CARDS.map((s) => (
          <div key={s.key} className="bg-[#141414] border border-border rounded-sm p-4">
            <s.icon className="w-5 h-5 text-blaze" />
            <div className="font-heading font-900 text-2xl mt-2 spec-block" data-testid={`stat-${s.key}`}>{stats?.[s.key] ?? "–"}</div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{s.label}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="reports">
        <TabsList className="bg-[#141414] border border-border rounded-sm p-1">
          <TabsTrigger value="reports" data-testid="admin-tab-reports" className="data-[state=active]:bg-blaze data-[state=active]:text-white rounded-sm font-bold uppercase text-xs tracking-wider">Reports</TabsTrigger>
          <TabsTrigger value="listings" data-testid="admin-tab-listings" className="data-[state=active]:bg-blaze data-[state=active]:text-white rounded-sm font-bold uppercase text-xs tracking-wider">All Listings</TabsTrigger>
        </TabsList>

        <TabsContent value="reports" className="mt-5 space-y-3">
          {reports.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-sm text-muted-foreground">No reports.</div>
          ) : reports.map((r) => (
            <div key={r.id} data-testid={`report-${r.id}`} className="bg-[#141414] border border-border rounded-sm p-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Badge className="bg-destructive text-white uppercase text-[10px] tracking-wider rounded-sm">{r.reason.replace("_", " ")}</Badge>
                  {r.status === "resolved" && <Badge className="bg-transparent border border-border text-muted-foreground uppercase text-[10px] rounded-sm">Resolved</Badge>}
                </div>
                <Link to={`/listing/${r.listing_id}`} className="font-heading font-800 text-sm mt-1.5 block hover:text-blaze truncate">{r.listing_title}</Link>
                {r.details && <p className="text-xs text-muted-foreground mt-0.5 truncate">{r.details}</p>}
                <p className="text-[11px] text-muted-foreground mt-1">Reported by {r.reporter_name}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button data-testid={`remove-listing-${r.listing_id}`} onClick={() => setStatus(r.listing_id, "removed")} variant="destructive" size="sm" className="rounded-sm text-xs">Remove Listing</Button>
                {r.status === "open" && <Button data-testid={`resolve-report-${r.id}`} onClick={() => resolve(r.id)} variant="outline" size="sm" className="border-border bg-transparent rounded-sm text-xs gap-1"><CheckCircle className="w-3.5 h-3.5" /> Resolve</Button>}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="listings" className="mt-5">
          <div className="border border-border rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#141414] text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Title</th>
                  <th className="text-left p-3 hidden sm:table-cell">Seller</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3 hidden sm:table-cell">Reports</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {listings.map((l) => (
                  <tr key={l.id} data-testid={`admin-listing-${l.id}`} className="border-t border-border hover:bg-[#141414]">
                    <td className="p-3"><Link to={`/listing/${l.id}`} className="hover:text-blaze font-medium flex items-center gap-1">{l.title}<Eye className="w-3 h-3 opacity-50" /></Link></td>
                    <td className="p-3 hidden sm:table-cell text-muted-foreground">{l.seller_name}</td>
                    <td className="p-3">
                      <Badge className={`uppercase text-[10px] rounded-sm ${l.status === "active" ? "bg-emerald-600" : "bg-destructive"} text-white`}>{l.status}</Badge>
                    </td>
                    <td className="p-3 hidden sm:table-cell spec-block">{l.report_count > 0 ? <span className="text-destructive font-bold">{l.report_count}</span> : "0"}</td>
                    <td className="p-3 text-right">
                      {l.status === "active" ? (
                        <Button data-testid={`admin-remove-${l.id}`} onClick={() => setStatus(l.id, "removed")} variant="destructive" size="sm" className="rounded-sm text-xs">Remove</Button>
                      ) : (
                        <Button data-testid={`admin-restore-${l.id}`} onClick={() => setStatus(l.id, "active")} variant="outline" size="sm" className="border-border bg-transparent rounded-sm text-xs">Restore</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
